import { useState, useEffect, useCallback, useMemo } from 'react';
import type { NodeChange } from '@xyflow/react';
import { useProjectStore } from '../store/projectStore';
import { GraphCanvas } from '../canvas/GraphCanvas';
import { ProjectsView } from './ProjectsView';
import { Palette } from '../panels/Palette';
import { Inspector } from '../panels/Inspector';
import { StatsPanel } from '../panels/StatsPanel';
import { ImportExportDialog } from '../panels/ImportExportDialog';
import { GraphTree } from './GraphTree';
import { downloadGraphPng, downloadGraphSvg } from '../canvas/exportCanvasImage';
import { LocalizationDialog } from '../panels/LocalizationDialog';
import { GraphLocation } from './GraphLocation';
import { SettingsView } from './SettingsView';
import { useSettingsNav, settingsNav } from '../store/settingsNav';
import { TilemapView } from '../../tilemap/TilemapView';
import { TilemapMenu } from '../../tilemap/panels/TilemapMenu';
import { tilemapStore } from '../../tilemap/store/tilemapStore';
import { tilemapNav, useTilemapNav } from '../../tilemap/store/tilemapNav';
import { PlayModePanel } from '../playMode/PlayModePanel';
import { PresentationMode } from '../playMode/PresentationMode';
import { toStoryGraph } from '../playMode/graphAdapter';

/**
 * A single action in the left sidebar's icon rail. Renders icon + label
 * when the sidebar is expanded, icon-only (with a title tooltip) when
 * collapsed — the same button, just missing its label, so collapsing the
 * sidebar never hides functionality, only the text.
 */
function SidebarActionButton({
    icon, label, onClick, disabled, collapsed, className = 'btn-outline-secondary',
}: {
    icon: string;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    collapsed: boolean;
    className?: string;
}) {
    return (
        <button
            type="button"
            className={`btn btn-sm ${className} w-100 d-flex align-items-center ${collapsed ? 'justify-content-center px-0' : 'gap-2'}`}
            onClick={onClick}
            disabled={disabled}
            title={label}
        >
            <i className={`ti ${icon}`} />
            {!collapsed && <span>{label}</span>}
        </button>
    );
}

export function EditorShell() {
    const { currentProject, currentGraph, nodes, edges, isDirty, saveGraph } = useProjectStore();
    const [view, setView] = useState<'projects' | 'editor'>('projects');
    const [showStats, setShowStats] = useState(false);
    const [showImportExport, setShowImportExport] = useState(false);
    const [showLocalization, setShowLocalization] = useState(false);
    const [leftTab, setLeftTab] = useState<'palette' | 'tree'>('palette');
    const [inspectorOpen, setInspectorOpen] = useState(true);
    const [showTilemapMenu, setShowTilemapMenu] = useState(false);
    const settingsOpen = useSettingsNav((s) => s.open);
    // Left sidebar now hosts every toolbar action (previously a separate top
    // navbar) — collapsing it to an icon-only rail reclaims canvas width
    // without losing any action, since every button keeps its icon.
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Play mode: 'panel' docks PlayModePanel where the Inspector normally
    // sits (side-by-side testing while editing); 'presentation' overlays
    // PresentationMode full-screen (playMode/README.md's two UI modes).
    const [playMode, setPlayMode] = useState<'off' | 'panel' | 'presentation'>('off');
    const storyGraph = useMemo(
        () => (currentGraph ? toStoryGraph(currentGraph.uuid, nodes, edges) : null),
        [currentGraph, nodes, edges],
    );
    const onNodeHighlight = useCallback((nodeId?: string) => {
        const state = useProjectStore.getState();
        const changes: NodeChange[] = state.nodes.map((n) => ({ id: n.id, type: 'select', selected: n.id === nodeId }));
        state.onNodesChange(changes);
    }, []);

    // Tile map editor: a second center view (_docs/tilemap/02 §1), driven by the
    // shared tilemapNav channel so a `tilemap_ref` node deep in the canvas can
    // request opening it. The map's own chunk autosave + optimistic locking make
    // the view swap non-destructive.
    const openTilemapUuid = useTilemapNav((s) => s.openUuid);

    const openTilemap = (tilemapUuid: string) => {
        setShowTilemapMenu(false);
        tilemapNav.getState().open(tilemapUuid);
    };

    const closeTilemap = () => {
        // Return to the source graph (if any), flushing pending edits first.
        const from = tilemapNav.getState().fromNode;
        void tilemapStore.getState().flush().finally(() => tilemapStore.getState().close());
        tilemapNav.getState().close();
        if (from) void useProjectStore.getState().openGraph(from.graphUuid);
        if (currentProject) window.history.pushState({}, '', `/p/${currentProject.uuid}`);
    };

    // Load the document + sync History whenever the open map changes.
    useEffect(() => {
        if (openTilemapUuid && currentProject) {
            void tilemapStore.getState().loadDocument(currentProject.uuid, openTilemapUuid);
            const path = `/p/${currentProject.uuid}/t/${openTilemapUuid}`;
            if (window.location.pathname !== path) {
                window.history.pushState({ tilemap: openTilemapUuid }, '', path);
            }
        }
    }, [openTilemapUuid, currentProject]);

    // Browser back/forward crosses the graph↔map view boundary naturally.
    useEffect(() => {
        const onPop = (e: PopStateEvent) => {
            const mapUuid = (e.state && (e.state as { tilemap?: string }).tilemap) || null;
            if (mapUuid) tilemapNav.getState().open(mapUuid);
            else tilemapNav.getState().close();
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    // Auto-save on interval (5 seconds after last change)
    useEffect(() => {
        if (!isDirty || !currentGraph) return;

        const timer = setTimeout(() => {
            saveGraph();
        }, 5000);

        return () => clearTimeout(timer);
    }, [isDirty, currentGraph, saveGraph]);

    // Switch to editor when a project is opened
    useEffect(() => {
        if (currentGraph) {
            setView('editor');
        }
    }, [currentGraph]);

    return (
        <>
            {settingsOpen ? (
                <SettingsView
                    onBack={() => {
                        settingsNav.getState().hide();
                        setView('projects');
                    }}
                />
            ) : view === 'projects' ? (
                <ProjectsView />
            ) : (
                <div className="d-flex flex-column h-100">
                    {openTilemapUuid && currentProject ? (
                        <div className="flex-fill" style={{ minHeight: 0 }}>
                            <TilemapView projectUuid={currentProject.uuid} onExit={closeTilemap} />
                        </div>
                    ) : (
                    <div className="d-flex flex-fill" style={{ minHeight: 0 }}>
                        <div
                            className="border-end d-flex flex-column"
                            style={{ width: sidebarCollapsed ? '48px' : '240px', flexShrink: 0 }}
                        >
                            {/* Collapse toggle + graph-location indicator (needs room, so hidden while collapsed) */}
                            <div className="d-flex align-items-center gap-1 border-bottom p-1">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary border-0"
                                    onClick={() => setSidebarCollapsed((c) => !c)}
                                    title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                                >
                                    <i className={`ti ti-chevron-${sidebarCollapsed ? 'right' : 'left'}`} />
                                </button>
                                {!sidebarCollapsed && <div className="flex-fill" style={{ minWidth: 0 }}><GraphLocation /></div>}
                            </div>

                            {/* Every toolbar action, previously a separate top navbar */}
                            <div className="d-flex flex-column gap-1 p-1 border-bottom">
                                <SidebarActionButton
                                    icon="ti-home"
                                    label="Back to Projects"
                                    onClick={() => setView('projects')}
                                    collapsed={sidebarCollapsed}
                                />

                                <div className={sidebarCollapsed ? 'dropend' : 'dropdown'}>
                                    <button
                                        type="button"
                                        className={`btn btn-sm btn-outline-secondary dropdown-toggle w-100 d-flex align-items-center ${sidebarCollapsed ? 'justify-content-center px-0' : 'gap-2'}`}
                                        data-bs-toggle="dropdown"
                                        title="Stats and tile maps"
                                    >
                                        <i className="ti ti-layout-grid" />
                                        {!sidebarCollapsed && <span>View</span>}
                                    </button>
                                    <div className="dropdown-menu">
                                        <button className="dropdown-item" onClick={() => setShowStats(true)}>
                                            <i className="ti ti-chart-bar me-2" /> Stats
                                        </button>
                                        <button className="dropdown-item" onClick={() => setShowTilemapMenu(true)}>
                                            <i className="ti ti-grid-4x4 me-2" /> Tile Maps
                                        </button>
                                    </div>
                                </div>

                                <div className={sidebarCollapsed ? 'dropend' : 'dropdown'}>
                                    <button
                                        type="button"
                                        className={`btn btn-sm btn-outline-secondary dropdown-toggle w-100 d-flex align-items-center ${sidebarCollapsed ? 'justify-content-center px-0' : 'gap-2'}`}
                                        data-bs-toggle="dropdown"
                                        title="Import, export, and localization"
                                    >
                                        <i className="ti ti-database-export" />
                                        {!sidebarCollapsed && <span>Data</span>}
                                    </button>
                                    <div className="dropdown-menu">
                                        <button className="dropdown-item" onClick={() => setShowImportExport(true)}>
                                            <i className="ti ti-package me-2" /> Export / Import Bundle
                                        </button>
                                        <button className="dropdown-item" onClick={() => setShowLocalization(true)}>
                                            <i className="ti ti-language me-2" /> Localization
                                        </button>
                                        <div className="dropdown-divider" />
                                        <button
                                            className="dropdown-item"
                                            onClick={() => downloadGraphSvg(nodes, edges, `${currentGraph?.name || 'graph'}.svg`)}
                                        >
                                            <i className="ti ti-photo me-2" /> Export Image (SVG)
                                        </button>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => downloadGraphPng(nodes, edges, `${currentGraph?.name || 'graph'}.png`)}
                                        >
                                            <i className="ti ti-photo me-2" /> Export Image (PNG)
                                        </button>
                                    </div>
                                </div>

                                <SidebarActionButton
                                    icon="ti-player-play"
                                    label="Play"
                                    onClick={() => setPlayMode(playMode === 'panel' ? 'off' : 'panel')}
                                    disabled={!storyGraph}
                                    collapsed={sidebarCollapsed}
                                    className={playMode === 'panel' ? 'btn-success' : 'btn-outline-success'}
                                />
                                <SidebarActionButton
                                    icon="ti-presentation"
                                    label="Present"
                                    onClick={() => setPlayMode('presentation')}
                                    disabled={!storyGraph}
                                    collapsed={sidebarCollapsed}
                                    className="btn-outline-success"
                                />
                                <SidebarActionButton
                                    icon="ti-download"
                                    label={isDirty ? 'Save' : 'Saved'}
                                    onClick={() => saveGraph()}
                                    disabled={!isDirty}
                                    collapsed={sidebarCollapsed}
                                    className="btn-outline-primary"
                                />
                            </div>

                            {/* Node palette / graph tree browser — hidden while collapsed, since
                                browsing/dragging from a 48px rail isn't practically usable. */}
                            {!sidebarCollapsed && (
                                <>
                                    <div className="d-flex border-bottom">
                                        <button
                                            className={`btn btn-sm flex-fill rounded-0 ${leftTab === 'palette' ? 'btn-light' : 'btn-white'}`}
                                            onClick={() => setLeftTab('palette')}
                                        >
                                            Nodes
                                        </button>
                                        <button
                                            className={`btn btn-sm flex-fill rounded-0 ${leftTab === 'tree' ? 'btn-light' : 'btn-white'}`}
                                            onClick={() => setLeftTab('tree')}
                                        >
                                            Tree
                                        </button>
                                    </div>
                                    <div className="overflow-auto flex-fill">
                                        {leftTab === 'palette' ? <Palette /> : <GraphTree />}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex-fill position-relative">
                            <GraphCanvas />
                        </div>
                        {playMode === 'panel' && storyGraph ? (
                            <div className="d-flex flex-column" style={{ width: '320px', flexShrink: 0 }}>
                                <PlayModePanel
                                    graph={storyGraph}
                                    projectUuid={currentProject?.uuid}
                                    onNodeHighlight={onNodeHighlight}
                                    onPlayModeChange={(isPlaying) => { if (!isPlaying) setPlayMode('off'); }}
                                />
                            </div>
                        ) : (
                            <div
                                className="border-start d-flex flex-column"
                                style={{ width: inspectorOpen ? '280px' : '36px', flexShrink: 0 }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary border-0 rounded-0"
                                    style={{ width: '36px', flexShrink: 0 }}
                                    title={inspectorOpen ? 'Collapse panel' : 'Expand panel'}
                                    onClick={() => setInspectorOpen((open) => !open)}
                                >
                                    <i className={`ti ti-chevron-${inspectorOpen ? 'right' : 'left'}`} />
                                </button>
                                {inspectorOpen && (
                                    <div className="overflow-auto flex-fill">
                                        <Inspector />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    )}

                    {playMode === 'presentation' && storyGraph && (
                        <PresentationMode
                            graph={storyGraph}
                            projectUuid={currentProject?.uuid}
                            onClose={() => setPlayMode('off')}
                        />
                    )}

                    {showTilemapMenu && currentProject && (
                        <TilemapMenu
                            projectUuid={currentProject.uuid}
                            onOpen={openTilemap}
                            onClose={() => setShowTilemapMenu(false)}
                        />
                    )}

                    {showStats && currentProject && (
                        <StatsPanel projectUuid={currentProject.uuid} onClose={() => setShowStats(false)} />
                    )}

                    {showImportExport && currentProject && (
                        <ImportExportDialog
                            projectUuid={currentProject.uuid}
                            restoreIntoProjectUuid={currentProject.uuid}
                            onClose={() => setShowImportExport(false)}
                        />
                    )}

                    {showLocalization && currentProject && (
                        <LocalizationDialog projectUuid={currentProject.uuid} onClose={() => setShowLocalization(false)} />
                    )}
                </div>
            )}
        </>
    );
}
